<template>
  <div class="projects">
    <section
      class="banner"
      v-if="banner"
    >
      <div class="container align--center">
        <h1
          class="title title__h1 bold"
          v-if="translate(banner.title)"
        >
          {{ translate(banner.title) }}
        </h1>
      </div>
    </section>

    <section
      class="map"
      v-if="map"
    >
      <div class="container">
        <app-google-maps
          class="map__wrapper"
          :markers="markers"
          :map-style="style"
        >
          <template #info_window="project">
            <project-map-card
              :project="project"
              v-if="project"
            >
              <template
                #content
                v-if="translate(map?.card?.cta)"
              >
                <span class="d-inline-block margin__t--6 link__color--prim bold">
                  {{ translate(map?.card?.cta) }}
                </span>
              </template>
            </project-map-card>
          </template>
        </app-google-maps>
      </div>
    </section>

    <section
      class="stats"
      v-if="stats?.metrics?.length"
    >
      <div class="container align--center">
        <app-counter
          :metrics="stats.metrics"
          :query="stats.query"
        />

        <app-link
          :link="stats.cta"
          class="margin__t--24 btn btn__color--white btn__size--classic bold d-inline-block"
        />
      </div>
    </section>

    <section
      class="actions"
      v-if="projects.length"
    >
      <div class="container align--center">
        <div class="row align--left justify-content-center">
          <template
            v-for="project in projects"
            :key="project.id"
          >
            <section class="col-12 col-sm-6 col-md-3">
              <project-card
                class="project-card"
                :project="project"
              />
            </section>
          </template>
        </div>

        <app-link
          class="btn btn__color--prim btn__size--classic d-inline-block margin__t--48"
          :link="actions.cta"
        />
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, defineAsyncComponent } from 'vue'
import { useProjectStore } from '@/stores/ProjectStore'
import { useI18n } from '@/vendors/i18n'
import { get as _get } from 'lodash'
import { svgToBase64 } from '@/utils/utils'

const AppLink = defineAsyncComponent(() => import('&/atoms/AppLink'))
const ProjectCard = defineAsyncComponent(() => import('&/organisms/ProjectCard'))
const AppGoogleMaps = defineAsyncComponent(() => import('&/atoms/AppGoogleMaps'))
const AppCounter = defineAsyncComponent(() => import('&/molecules/AppCounter'))
const ProjectMapCard = defineAsyncComponent(() => import('&/organisms/ProjectMapCard'))

const { sections } = defineProps({
  sections: { type: Object, required: true }
})

const { translate } = useI18n()
const { getProjects } = useProjectStore()

const { banner, map, stats, actions } = sections.attributes

const icon = svgToBase64('<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 32 45" width="32" height="45" style="enable-background:new 0 0 32 45;" xml:space="preserve" fill="#46935A"><path fill-rule="evenodd" clip-rule="evenodd" d="M16.1,0C24.9,0,32,7.2,32,16.1c0,8.8-15.3,28.7-15.9,28.7C15.5,44.7,0,24.9,0,16.1C0,7.2,7.1,0,16.1,0z M16.1,10.1c3,0,5.5,2.5,5.5,5.5s-2.5,5.5-5.5,5.5s-5.5-2.5-5.5-5.5S13,10.1,16.1,10.1z"/></svg>')
const style = [{ 'featureType':'all','elementType':'geometry.fill','stylers':[{ 'visibility':'on' }] },{ 'featureType':'administrative','elementType':'all','stylers':[{ 'color':'#f2f2f2' }] },{ 'featureType':'administrative','elementType':'labels.text.fill','stylers':[{ 'color':'#686868' },{ 'visibility':'on' }] },{ 'featureType':'landscape','elementType':'all','stylers':[{ 'color':'#f2f2f2' }] },{ 'featureType':'poi','elementType':'all','stylers':[{ 'visibility':'off' }] },{ 'featureType':'poi.park','elementType':'all','stylers':[{ 'visibility':'on' }] },{ 'featureType':'poi.park','elementType':'labels.icon','stylers':[{ 'visibility':'off' }] },{ 'featureType':'road','elementType':'all','stylers':[{ 'saturation':-100 },{ 'lightness':45 },{ 'visibility':'off' }] },{ 'featureType':'road','elementType':'geometry','stylers':[{ 'visibility':'off' }] },{ 'featureType':'road.highway','elementType':'all','stylers':[{ 'visibility':'off' }] },{ 'featureType':'road.highway','elementType':'geometry.fill','stylers':[{ 'lightness':'-22' },{ 'visibility':'on' },{ 'color':'#b4b4b4' }] },{ 'featureType':'road.highway','elementType':'geometry.stroke','stylers':[{ 'saturation':'-51' },{ 'lightness':'11' }] },{ 'featureType':'road.highway','elementType':'labels.text','stylers':[{ 'saturation':'3' },{ 'lightness':'-56' },{ 'visibility':'simplified' }] },{ 'featureType':'road.highway','elementType':'labels.text.fill','stylers':[{ 'lightness':'-52' },{ 'color':'#9094a0' },{ 'visibility':'simplified' }] },{ 'featureType':'road.highway','elementType':'labels.text.stroke','stylers':[{ 'weight':'6.13' }] },{ 'featureType':'road.highway','elementType':'labels.icon','stylers':[{ 'weight':'1.24' },{ 'saturation':'-100' },{ 'lightness':'-10' },{ 'gamma':'0.94' },{ 'visibility':'off' }] },{ 'featureType':'road.highway.controlled_access','elementType':'geometry.fill','stylers':[{ 'visibility':'on' },{ 'color':'#b4b4b4' },{ 'weight':'5.40' },{ 'lightness':'7' }] },{ 'featureType':'road.highway.controlled_access','elementType':'labels.text','stylers':[{ 'visibility':'simplified' },{ 'color':'#231f1f' }] },{ 'featureType':'road.highway.controlled_access','elementType':'labels.text.fill','stylers':[{ 'visibility':'simplified' },{ 'color':'#595151' }] },{ 'featureType':'road.arterial','elementType':'geometry','stylers':[{ 'lightness':'-16' }] },{ 'featureType':'road.arterial','elementType':'geometry.fill','stylers':[{ 'visibility':'on' },{ 'color':'#d7d7d7' }] },{ 'featureType':'road.arterial','elementType':'labels.text','stylers':[{ 'color':'#282626' },{ 'visibility':'simplified' }] },{ 'featureType':'road.arterial','elementType':'labels.text.fill','stylers':[{ 'saturation':'-41' },{ 'lightness':'-41' },{ 'color':'#2a4592' },{ 'visibility':'simplified' }] },{ 'featureType':'road.arterial','elementType':'labels.text.stroke','stylers':[{ 'weight':'1.10' },{ 'color':'#ffffff' }] },{ 'featureType':'road.arterial','elementType':'labels.icon','stylers':[{ 'visibility':'on' }] },{ 'featureType':'road.local','elementType':'geometry.fill','stylers':[{ 'lightness':'-16' },{ 'weight':'0.72' }] },{ 'featureType':'road.local','elementType':'labels.text.fill','stylers':[{ 'lightness':'-37' },{ 'color':'#2a4592' }] },{ 'featureType':'transit','elementType':'all','stylers':[{ 'visibility':'off' }] },{ 'featureType':'transit.line','elementType':'geometry.fill','stylers':[{ 'visibility':'off' },{ 'color':'#eeed6a' }] },{ 'featureType':'transit.line','elementType':'geometry.stroke','stylers':[{ 'visibility':'off' },{ 'color':'#0a0808' }] },{ 'featureType':'water','elementType':'all','stylers':[{ 'color':'#b7e4f4' },{ 'visibility':'on' }] }]

const projectsQuery = { per_page: 4, sort_by: 'amount_first', ..._get(actions, 'query', {}) }

const { projects } = getProjects({ query: projectsQuery, dependencies: [actions] })
const { projects: projectLocations } = getProjects({ query: { ...projectsQuery, view_format: 'locations' }, dependencies: [map] })

const markers = computed(() => projectLocations.value.map(project => ({ ...project, position: { lat: _get(project, 'address.latitude'), lng: _get(project, 'address.longitude') }, icon })))
</script>

<style lang="scss" scoped>
$color--bg: #FFFFFF;
$color--primary: #46935A;
$color--secondary: #30643D;
$color--tertiary: #F3FFD3;

:deep() {
  .btn {
    line-height: 1.18;

    &__size {
      &--classic {
        padding-top: 12px;
        padding-bottom: 12px;
        border-radius: 30px;
        font-size: rem(18px);

        @include mq(md) {
          padding-top: 16px;
          padding-bottom: 16px;
          font-size: rem(20px);
        }
      }
    }

    &__color {
      &--white {
        background: $color--bg;

        &:hover {
          color: $white;
          background: shade(hsl(135, 35%, 29%));
        }
      }
    }
  }
}


.title {
  font-family: $font-family--secondary;
  color: #4C5562;
  position: relative;
  z-index: 10;

  &__h1 {
    font-weight: 700;
    font-size: rem(32px);
    line-height: 1.208;

    @include mq(md) {
      font-size: rem(48px);
    }
  }
}

.projects {
  background: $color--bg;
}

.banner {
  padding: 24px 0;
}

.map {
  padding-bottom: 24px;

  &:last-child {
    margin-bottom: 60px;
  }

  &:first-child {
    padding-top: 24px;
  }

  &__wrapper {
    border-radius: 20px;
    overflow: hidden;
    position: relative;

    @include aspect-ratio(1272, 801);
  }
}

.stats {
  background: $color--primary;
  color: $white;
  font-size: rem(18px);
  line-height: 0.75;
  padding: 48px 0;

  @include mq(lg) {
    font-size: rem(24px);
  }

  :deep() {
    .counter {
      &__ul {
        gap: 32px;
        display: grid;
        grid-template-columns: 1fr;

        @include mq(sm) {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          gap: 24px;
        }
      }

      &__li {
        flex: 1;
      }

      &__value {
        font-weight: 700;
        font-size: rem(50px);
        line-height: 0.3;
        white-space: nowrap;

        @include mq(md) {
          font-size: rem(60px);
        }

        @include mq(lg) {
          font-size: rem(80px);
        }
      }

      &__text {
        display: block;
        margin-top: 18px;
      }
    }
  }
}

.actions {
  padding: 60px 0;
}
</style>